import { Alert, Autocomplete, Box, Stack, TextField } from '@mui/material';
import { Role, UserIdentityProvider, Visitor } from 'graphql/generated/graphqlTypes';
import useAddOrEditVisitor from 'hooks/allVisitors/drawers/useAddOrEditVisitor';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { getVisitorGroupById, VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { VISITOR_NAME_MAX_LENGTH } from 'v2/constants';
import { getUserPath } from 'v2/paths';
import { emailRegex } from 'v2/utils/validation';
import { getAutocompleteVisitorGroupOptions, VisitorGroupAutocompleteOption } from 'v2/utils/visitorGroupAutocomplete';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import RolesSection from './RolesSection';

interface Props {
  preSelectedGroup?: VisitorGroupAutocompleteOption | null;
  hideGroupField?: boolean;
  visitor?: Visitor;
  topContent?: ReactNode;
  isMaxNumberOfUsersReached?: boolean;
}

export interface AddUserFormValues {
  name: string;
  email: string;
  visitorGroup: VisitorGroupAutocompleteOption[];
  isAdministratorRole: boolean;
  isInstallerRole: boolean;
}

const FORM_ID = 'add-user-drawer-form';

const UserFormDrawer = ({
  preSelectedGroup,
  hideGroupField,
  visitor,
  topContent,
  isMaxNumberOfUsersReached
}: Props) => {
  const isEditMode = visitor !== undefined;
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const {
    coContractors,
    coContractorsLoading,
    addOrEditVisitor,
    administratorsVisitorGroups,
    administratorsVisitorGroupsLoading,
    installersVisitorGroups,
    installersVisitorGroupsLoading
  } = useAddOrEditVisitor({
    translation,
    handleFetchError: setError
  });

  const isLoading = coContractorsLoading || administratorsVisitorGroupsLoading || installersVisitorGroupsLoading;

  const preSelectedVisitorGroup =
    preSelectedGroup && administratorsVisitorGroups && installersVisitorGroups
      ? getVisitorGroupById([...administratorsVisitorGroups, ...installersVisitorGroups], preSelectedGroup.value)
      : undefined;
  const isAdministratorsGroup = preSelectedVisitorGroup?.type === VisitorGroupType.Administrators;
  const isInstallersGroup = preSelectedVisitorGroup?.type === VisitorGroupType.Installers;

  const defaultAddUserFormValues: AddUserFormValues = useMemo(
    () => ({
      name: visitor?.name ?? '',
      email: visitor?.email ?? '',
      visitorGroup: preSelectedGroup ? [preSelectedGroup] : [],
      isAdministratorRole: visitor?.isAdmin ?? isAdministratorsGroup,
      isInstallerRole:
        visitor?.visitorGroupMembership.some((m) => m.visitorGroup.type === VisitorGroupType.Installers) ??
        isInstallersGroup
    }),
    [visitor, preSelectedGroup, isAdministratorsGroup, isInstallersGroup]
  );

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isSubmitting, isDirty }
  } = useForm<AddUserFormValues>({
    defaultValues: defaultAddUserFormValues
  });

  useEffect(() => {
    if (!isLoading) {
      reset(defaultAddUserFormValues);
    }
  }, [isLoading, reset, defaultAddUserFormValues]);

  const isAdministratorRole = watch('isAdministratorRole');
  const isInstallerRole = watch('isInstallerRole');

  const isEntraUser = visitor?.identityProvider === UserIdentityProvider.Entra;

  const visitorOrigin = visitor ? getVisitorOrigin(visitor) : undefined;
  const isKeySharingVisitor = visitorOrigin === VisitorOrigin.KeySharing;
  const showRolesSection = !isKeySharingVisitor;

  const onSubmit = async (data: AddUserFormValues) => {
    setError(undefined);
    // Backend rejects SetVisitorByIdWithRole when the membership hasn't actually changed.
    // Skip the call and close cleanly when nothing was edited.
    if (isEditMode && !isDirty) {
      closeDrawer();
      return;
    }
    const result = await addOrEditVisitor({
      isNewVisitor: !isEditMode,
      name: data.name,
      email: data.email,
      id: visitor?.id,
      visitorGroupMembershipInput: isEditMode
        ? visitor.visitorGroupMembership.map((m) => ({ role: m.role, visitorGroupId: m.visitorGroup.id }))
        : data.visitorGroup.map((group) => ({ visitorGroupId: group.value, role: Role.None })),
      shouldBeAdmin: data.isAdministratorRole,
      shouldBeInstaller: data.isInstallerRole
    });
    if (result?.data && result.errors === undefined) {
      showSnackbar(translation.saved_changes, { severity: 'success' });
      closeDrawer();
      const newVisitorId = 'addVisitor' in result.data ? result.data.addVisitor?.id : undefined;
      if (newVisitorId && !preSelectedGroup) {
        navigate(getUserPath(newVisitorId));
      }
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={isEditMode ? translation.edit_user : translation.add_user}
      primaryAction={{
        label: isEditMode ? translation.save : translation.add,
        onClick: handleSubmit(onSubmit),
        loading: isLoading || isSubmitting,
        form: FORM_ID,
        id: 'drawer-submit-button',
        disabled: isMaxNumberOfUsersReached
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      errorTitle={isEditMode ? translation.could_not_save_changes : translation.could_not_add_user}
      onDismissError={() => setError(undefined)}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      ) : (
        <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={3}>
          {topContent}
          <TextField
            {...register('name', {
              required: translation.name_cannot_be_empty,
              maxLength: {
                value: VISITOR_NAME_MAX_LENGTH,
                message: translation.name_max_length_error.replace('{{count}}', String(VISITOR_NAME_MAX_LENGTH))
              }
            })}
            id="name-input"
            label={translation.name}
            variant="outlined"
            inputProps={{ readOnly: isEntraUser }}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            {...register('email', {
              required: !isEditMode && translation.please_enter_an_email_address,
              pattern: !isEditMode
                ? { value: emailRegex, message: translation.this_is_not_a_correct_email_address }
                : undefined,
              validate: !isEditMode
                ? (value) =>
                    coContractors.some(({ members }) => members.some((member) => member.email === value))
                      ? translation.email_has_already_been_added
                      : true
                : undefined
            })}
            id="email-input"
            label={translation.e_mail}
            variant="outlined"
            disabled={isEditMode}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          {isEntraUser && <Alert severity="info">{translation.managed_through_entra_id}</Alert>}
          {!hideGroupField && !isEditMode && (
            <Controller
              name="visitorGroup"
              control={control}
              rules={{
                validate: (value) =>
                  isAdministratorRole || isInstallerRole || value.length > 0 ? true : translation.group_cannot_be_empty
              }}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  value={field.value}
                  onChange={(_event, newValue) => field.onChange(newValue)}
                  options={getAutocompleteVisitorGroupOptions(coContractors)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
                  renderOption={(optionProps, option, state) => {
                    const { key, ...rest } = optionProps;
                    return (
                      <li key={key} {...rest} id={`option-${state.index}`}>
                        {option.label}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, id: 'user-groups-input' }}
                      label={translation.user_groups}
                      error={!!errors.visitorGroup}
                      helperText={errors.visitorGroup?.message}
                    />
                  )}
                />
              )}
            />
          )}
          {showRolesSection && (
            <RolesSection
              control={control}
              disableAdministratorRole={isAdministratorsGroup || isEntraUser}
              disableInstallerRole={isInstallersGroup}
              administratorDisabledTooltip={
                isEntraUser
                  ? translation.admin_role_entra_id_tooltip
                  : isAdministratorsGroup
                    ? translation.role_implied_by_selected_group
                    : undefined
              }
              installerDisabledTooltip={isInstallersGroup ? translation.role_implied_by_selected_group : undefined}
            />
          )}
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default UserFormDrawer;
